import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as S from "./styles";
import { ROLE_LABELS } from "./types";
import { useAuditAccess } from "./useAuditAccess";

// ----------------------------  SVG Icons  ----------------------------

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

/** Bullet-list icon (three rows: dot + line) */
const ListIcon: React.FC = () => (
  <svg {...iconProps}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

/** Microscope icon (workspace / review) */
const MicroscopeIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M6 18h8" />
    <path d="M3 22h18" />
    <path d="M14 22a7 7 0 1 0 0-14h-1" />
    <path d="M9 14h2" />
    <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z" />
    <path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3" />
  </svg>
);

/** Gear / cog icon (circle center + toothed ring) */
const SettingsIcon: React.FC = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

/** Download icon (tray + arrow) */
const DownloadIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const NAV_ICONS: Record<string, React.FC> = {
  list: ListIcon,
  microscope: MicroscopeIcon,
  settings: SettingsIcon,
  download: DownloadIcon,
};

// ----------------------------  Nav Items  ----------------------------

interface NavSubItem {
  key: string;
  label: string;
  path: string;
  matchPrefix: string;
}

interface NavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  matchPrefix: string;
  contextual?: boolean;
  // Admins and Program Owners only
  managerOnly?: boolean;
  badge?: string;
  children?: NavSubItem[];
}

const NAV_ITEMS: NavItem[] = [
  {
    key: "queue",
    label: "Audit Queue",
    icon: "list",
    path: "/vlmAudit/my-tasks",
    matchPrefix: "/vlmAudit/my-tasks|/vlmAudit/completed|/vlmAudit/expired",
  },
  {
    key: "workspace",
    label: "Audit Workspace",
    icon: "microscope",
    path: "/vlmAudit/workspace",
    matchPrefix: "/vlmAudit/workspace",
    contextual: true,
  },
  {
    key: "reports",
    label: "Export & Reports",
    icon: "download",
    path: "/vlmAudit/reports/export",
    matchPrefix: "/vlmAudit/reports",
    managerOnly: true,
    children: [
      {
        key: "reports-export",
        label: "Export Builder",
        path: "/vlmAudit/reports/export",
        matchPrefix: "/vlmAudit/reports/export",
      },
    ],
  },
  {
    key: "config",
    label: "Configuration",
    icon: "settings",
    path: "/vlmAudit/config",
    matchPrefix: "/vlmAudit/config",
    managerOnly: true,
    children: [
      {
        key: "config-uc",
        label: "Use Case Management",
        path: "/vlmAudit/config",
        matchPrefix: "/vlmAudit/config",
      },
      {
        key: "config-perms",
        label: "Permissions & Access",
        path: "/vlmAudit/config/permissions",
        matchPrefix: "/vlmAudit/config/permissions",
      },
    ],
  },
];

// ----------------------------  AuditLayout  ----------------------------

interface AuditLayoutProps {
  children: React.ReactNode;
  /** When set, the Workspace nav item links to this task and becomes active. */
  activeTaskId?: string;
}

const AuditLayout: React.FC<AuditLayoutProps> = ({ children, activeTaskId }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Configuration (Use Case Management + Permissions & Access) and Export & Reports
  // are restricted to Admins and Program Owners. The role comes from the permissions
  // registry (GET /audit_access/me), not LDAP. Auditors never see either module.
  const { canManageConfig, role } = useAuditAccess();
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.managerOnly || canManageConfig).map(
    (item) => (item.key === "config" && role ? { ...item, badge: ROLE_LABELS[role] } : item),
  );

  const hasActiveTask = Boolean(activeTaskId);
  const workspacePath = activeTaskId
    ? "/vlmAudit/workspace/" + activeTaskId
    : "/vlmAudit/workspace";

  const isActive = (item: { matchPrefix: string }): boolean => {
    const prefixes = item.matchPrefix.split("|");
    return prefixes.some((p) => location.pathname.startsWith(p));
  };

  const isSubActive = (sub: NavSubItem): boolean => {
    // For the "Use Case Management" sub-item, match /vlmAudit/config exactly
    // or /vlmAudit/config/<uuid> but NOT /vlmAudit/config/permissions.
    if (sub.key === "config-uc") {
      const path = location.pathname;
      return (
        path.startsWith("/vlmAudit/config") && !path.startsWith("/vlmAudit/config/permissions")
      );
    }
    return location.pathname.startsWith(sub.matchPrefix);
  };

  // Special case: bare /vlmAudit path should highlight queue
  const isQueueDefault = location.pathname === "/vlmAudit" || location.pathname === "/vlmAudit/";

  return (
    <div style={S.auditLayoutWrapper}>
      {/* Sidebar */}
      <nav style={S.auditSidebar}>
        {visibleNavItems.map((item) => {
          const isWorkspace = item.contextual;
          const active = item.key === "queue" ? isActive(item) || isQueueDefault : isActive(item);

          // Contextual Workspace item: dimmed/disabled when no active task
          if (isWorkspace && !hasActiveTask) {
            return (
              <div
                key={item.key}
                style={sidebarDisabled}
                title="Opens when you claim or open a task"
              >
                {React.createElement(NAV_ICONS[item.icon] ?? ListIcon)}
                <span>{item.label}</span>
                <span style={contextualBadge}>Contextual</span>
              </div>
            );
          }

          const to = isWorkspace ? workspacePath : item.path;

          return (
            <React.Fragment key={item.key}>
              <button
                style={active ? S.sidebarLinkActive : S.sidebarLink}
                onClick={() => navigate(to)}
              >
                {React.createElement(NAV_ICONS[item.icon] ?? ListIcon)}
                <span>{item.label}</span>
                {item.badge && <span style={adminBadge}>{item.badge}</span>}
              </button>
              {item.children && active && (
                <div style={subNavGroup}>
                  {item.children.map((sub) => (
                    <button
                      key={sub.key}
                      style={isSubActive(sub) ? subNavActive : subNavLink}
                      onClick={() => navigate(sub.path)}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Main content area */}
      <div style={S.auditMainContent}>{children}</div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const adminBadge: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: "0.625rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  background: "rgba(30, 58, 138, 0.1)",
  color: "#1e3a8a",
  padding: "2px 6px",
  borderRadius: "4px",
};

const contextualBadge: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: "0.625rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  background: "#f3f4f6",
  color: "#9ca3af",
  padding: "2px 6px",
  borderRadius: "4px",
};

const subNavGroup: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  marginLeft: "36px",
  marginBottom: "4px",
};

const subNavBase: React.CSSProperties = {
  display: "block",
  padding: "6px 16px",
  fontSize: "0.875rem",
  fontWeight: 500,
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  textAlign: "left" as const,
  width: "100%",
};

const subNavLink: React.CSSProperties = {
  ...subNavBase,
  background: "none",
  color: "#6b7280",
};

const subNavActive: React.CSSProperties = {
  ...subNavBase,
  background: "#e6f7f7",
  color: "#0f7b8a",
};

const sidebarDisabled: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  margin: "0 8px",
  fontSize: "1rem",
  fontWeight: 500,
  color: "rgba(75, 85, 99, 0.4)",
  background: "none",
  border: "none",
  borderRadius: "6px",
  cursor: "not-allowed",
  width: "calc(100% - 16px)",
  textAlign: "left" as const,
  userSelect: "none" as const,
};

export default AuditLayout;
